export interface TreeCounts {
  nodes?: Record<string, number | undefined>;
  branches?: Record<string, number | undefined>;
  outcomes?: Record<string, number | undefined>;
}
type Point = [number, number];
type Bounds = [number, number, number, number];
interface Card {
  id: string;
  label: string;
  icon: string;
  bounds: Bounds;
  value: number;
  warning: boolean;
  muted: boolean;
  threshold: boolean;
}
interface Branch {
  id: string;
  point: Point;
  labelPoint: Point;
  yes: boolean;
  value: number;
  muted: boolean;
  side: boolean;
}
export interface RenderedTree {
  width: number;
  height: number;
  root: Bounds;
  title: Point;
  rootValue: number;
  ytd: boolean;
  cards: Card[];
  outcomes: Card[];
  branches: Branch[];
  paths: {
    path: string;
    arrow: boolean;
    muted: boolean;
    tip: Point;
    angle: number;
  }[];
}

export function buildRenderedTree(
  sellout: boolean,
  vilc: boolean,
  ytd: boolean,
  counts: TreeCounts,
  version: "V2" | "V3" = "V3",
): RenderedTree {
  ytd = ytd && version === "V3";
  const extended = {
    width: 1460,
    height: 971,
    root: [8, 210, 184, 311] as Bounds,
    title: [42, 186] as Point,
    high: [438, 123, 103, 133] as Bounds,
    profitHigh: [723, 120, 119, 134] as Bounds,
    assess: [255, 589, 125, 144] as Bounds,
    low: [602, 418, 103, 135] as Bounds,
    focus: [894, 418, 119, 135] as Bounds,
    profitLow: [894, 604, 119, 134] as Bounds,
    filter: [469, 814, 112, 142] as Bounds,
    recovery: [799, 803, 106, 135] as Bounds,
    outcomes: [
      [1220, 20],
      [1223, 309],
      [1221, 452],
      [1221, 612],
      [1220, 722],
      [1221, 843],
    ] as Point[],
    junction: 429,
    returnX: 527,
    recoveryX: 655,
    recoveryY: 700,
  };
  const compact = {
    width: 1354,
    height: 814,
    root: [0, 184, 184, 311] as Bounds,
    title: [29, 161] as Point,
    high: [584, 66, 103, 134] as Bounds,
    profitHigh: [890, 63, 103, 135] as Bounds,
    assess: [241, 443, 125, 144] as Bounds,
    low: [588, 273, 103, 134] as Bounds,
    focus: [890, 275, 103, 135] as Bounds,
    profitLow: [890, 443, 103, 134] as Bounds,
    filter: [455, 668, 112, 142] as Bounds,
    recovery: [785, 657, 106, 135] as Bounds,
    outcomes: [
      [1132, 10],
      [1132, 130],
      [1132, 306],
      [1132, 466],
      [1132, 582],
      [1132, 697],
    ] as Point[],
    junction: 415,
    returnX: 513,
    recoveryX: 641,
    recoveryY: 554,
  };
  const layout = vilc ? extended : compact;
  function value(group: keyof TreeCounts, id: string): number {
    const result = counts[group]?.[id] ?? 0;
    return Number.isFinite(result) && result >= 0 ? result : 0;
  }
  const graph: RenderedTree = {
    ...layout,
    rootValue: value("nodes", "volume"),
    ytd,
    cards: [],
    outcomes: [],
    branches: [],
    paths: [],
  };
  function wire(source: Point[], arrow = true, muted = false): void {
    const points = source.map((point) => [...point] as Point);
    const endpoint = points[points.length - 1];
    const previous = points[points.length - 2];
    const angle =
      (Math.atan2(endpoint[1] - previous[1], endpoint[0] - previous[0]) * 180) /
      Math.PI;
    if (arrow) {
      endpoint[0] -= Math.sign(endpoint[0] - previous[0]) * 8;
      endpoint[1] -= Math.sign(endpoint[1] - previous[1]) * 8;
    }
    const tip: Point = [...endpoint];
    if (arrow) {
      endpoint[0] -= Math.sign(endpoint[0] - previous[0]) * 17;
      endpoint[1] -= Math.sign(endpoint[1] - previous[1]) * 17;
    }
    let path = `M${points[0].join(" ")}`;
    for (let index = 1; index < points.length - 1; index++) {
      const previous = points[index - 1];
      const current = points[index];
      const next = points[index + 1];
      const incoming = Math.min(
        12,
        Math.hypot(current[0] - previous[0], current[1] - previous[1]) / 2,
      );
      const outgoing = Math.min(
        12,
        Math.hypot(next[0] - current[0], next[1] - current[1]) / 2,
      );
      const entry = current.map(
        (coordinate, axis) =>
          coordinate - Math.sign(coordinate - previous[axis]) * incoming,
      );
      const exit = current.map(
        (coordinate, axis) =>
          coordinate + Math.sign(next[axis] - coordinate) * outgoing,
      );
      path += ` L${entry.join(" ")} Q${current.join(" ")} ${exit.join(" ")}`;
    }
    path += ` L${points[points.length - 1].join(" ")}`;
    graph.paths.push({ path, arrow, muted, tip, angle });
  }
  function card(
    id: string,
    label: string,
    icon: string,
    bounds: Bounds,
    warning = false,
    muted = false,
    threshold = false,
  ): void {
    graph.cards.push({
      id,
      label,
      icon,
      bounds,
      warning: warning && ytd,
      muted,
      threshold,
      value: value("nodes", id),
    });
  }
  function branch(
    id: string,
    point: Point,
    yes: boolean,
    side = false,
    muted = false,
    left = false,
  ): void {
    const labelPoint: Point = side
      ? [point[0] + (left ? -48 : 24), point[1] - 8]
      : [point[0], point[1] - 40];
    graph.branches.push({
      id,
      point,
      yes,
      side,
      muted,
      labelPoint,
      value: value("branches", id),
    });
  }
  const highY = vilc ? 187 : 131;
  const lowY = vilc ? 489 : 343;
  const assessY = vilc ? 658 : 512;
  const filterY = vilc ? 880 : 734;
  const profitY = vilc ? 650 : 503;
  const rootX = vilc ? 201 : 197;
  const rootLowX = rootX;
  const assessEntryY = vilc ? 664 : 509;
  const lowerX = layout.profitLow[0] + layout.profitLow[2] / 2;
  const outputX = layout.outcomes[0][0];
  const muted = !sellout;
  wire(
    [
      [layout.root[0] + layout.root[2], 358],
      [rootX, 358],
    ],
    false,
  );
  wire([
    [rootX, 358],
    [rootX, highY],
    [layout.high[0], highY],
  ]);
  wire([
    [rootLowX, 358],
    [rootLowX, assessEntryY],
    [layout.assess[0], assessEntryY],
  ]);
  wire([
    [layout.high[0] + layout.high[2], highY],
    [layout.profitHigh[0], highY],
  ]);
  wire(
    [
      [layout.assess[0] + layout.assess[2], assessY],
      [layout.junction, assessY],
    ],
    false,
  );
  wire([
    [layout.junction, assessY],
    [layout.junction, lowY],
    [layout.low[0], lowY],
  ]);
  wire([
    [layout.junction, assessY],
    [layout.junction, filterY],
    [layout.filter[0], filterY],
  ]);
  wire([
    [layout.returnX, layout.filter[1]],
    [layout.returnX, lowY],
  ]);
  wire([
    [layout.low[0] + layout.low[2], lowY],
    [layout.focus[0], lowY],
  ]);
  wire([
    [layout.focus[0] + layout.focus[2], lowY],
    [layout.outcomes[2][0], lowY],
  ]);
  wire(
    [
      [lowerX, layout.focus[1] + layout.focus[3]],
      [lowerX, layout.profitLow[1]],
    ],
    false,
  );
  wire([
    [layout.profitLow[0] + layout.profitLow[2], profitY],
    [layout.outcomes[3][0], profitY],
  ]);
  wire(
    vilc
      ? [
          [lowerX, 738],
          [lowerX, 757],
          [outputX, 757],
        ]
      : [
          [lowerX, 577],
          [lowerX, 650],
          [1082, 650],
          [1082, 618],
          [outputX, 618],
        ],
  );
  wire([
    [layout.filter[0] + layout.filter[2], filterY],
    [layout.recovery[0], filterY],
  ]);
  wire([
    [layout.recovery[0] + layout.recovery[2], filterY],
    [layout.outcomes[5][0], filterY],
  ]);
  wire(
    [
      [layout.recovery[0] + 60, layout.recovery[1]],
      [layout.recovery[0] + 60, layout.recoveryY],
      [layout.recoveryX, layout.recoveryY],
      [layout.recoveryX, layout.low[1] + layout.low[3]],
    ],
    true,
    muted,
  );
  branch("volume-yes", [rootX, vilc ? 283 : 257], true);
  branch("volume-no", [rootLowX, vilc ? 508 : 407], false, true, false, true);
  branch("assess-yes", [layout.junction + 37, lowY], true);
  branch("assess-no", [layout.junction, vilc ? 756 : 610], false, true);
  branch("filter-yes", [layout.returnX, assessY + 4], true, true);
  branch("filter-no", [vilc ? 686 : 672, filterY], false);
  branch("focus-yes", [vilc ? 1115 : 1056, lowY], true);
  branch("focus-no", [lowerX, vilc ? 584 : 427], false, true);
  branch("profit-low-yes", [vilc ? 1115 : 1034, profitY], true);
  branch("profit-low-no", [vilc ? 1115 : 1034, vilc ? 757 : 650], false);
  branch(
    "recovery-yes",
    [vilc ? 762 : 748, layout.recoveryY],
    true,
    false,
    muted,
  );
  branch("recovery-no", [vilc ? 1115 : 1034, filterY], false);
  if (vilc) {
    wire(
      [
        [786, 120],
        [786, 60],
        [972, 60],
      ],
      false,
    );
    wire([
      [786, 254],
      [786, 353],
      [1223, 353],
    ]);
    wire([
      [1072, 60],
      [1220, 60],
    ]);
    wire(
      [
        [1024, 144],
        [1024, 188],
      ],
      false,
      muted,
    );
    wire(
      [
        [1022, 290],
        [1022, 353],
      ],
      false,
      muted,
    );
    wire(
      [
        [1072, 239],
        [1120, 239],
      ],
      true,
      muted,
    );
    wire(
      [
        [1176, 154],
        [1176, 60],
      ],
      false,
      muted,
    );
    wire(
      [
        [1176, 290],
        [1176, 353],
      ],
      false,
      muted,
    );
    branch("profit-high-no", [786, 88], false, true);
    branch("profit-high-yes", [786, 322], true, true);
    branch("index-no", [1121, 59], false);
    branch("index-yes", [1024, 166], true, true, muted);
    branch("mb-yes", [1022, 322], true, true, muted);
    branch("mb-no", [1093, 239], false, false, muted);
    branch("retention-no", [1176, 106], false, true, muted);
    branch("retention-yes", [1176, 320], true, true, muted);
    card(
      "index",
      "Is it above\nNR & VILC\nIndex?",
      "index-value",
      [972, 11, 100, 133],
      true,
    );
    card("mb", "Is MB?", "mb", [972, 188, 100, 102], false, muted);
    card(
      "retention",
      "Volume Retention\nTest",
      "retention",
      [1120, 154, 116, 136],
      false,
      muted,
    );
  } else {
    wire([
      [933, 63],
      [933, 41],
      [1132, 41],
    ]);
    wire([
      [993, 162],
      [1132, 162],
    ]);
    branch(
      sellout ? "profit-high-no" : "profit-high-yes",
      [1063, 41],
      !sellout,
    );
    branch(
      sellout ? "profit-high-yes" : "profit-high-no",
      [1063, 162],
      sellout,
    );
  }
  card("high", "Maintain\n(High Volume)", "maintain-high", layout.high, true);
  card(
    "profit-high",
    "Profitable?",
    "profitable",
    layout.profitHigh,
    true,
    false,
    vilc,
  );
  card("assess", "Assess\nStrategic Value", "strategic-value", layout.assess);
  card("low", "Maintain\n(Low Volume)", "maintain-low", layout.low, true);
  card("focus", "Focus Brand?", "focus-brand", layout.focus);
  card(
    "profit-low",
    "Profitable?",
    "profitable",
    layout.profitLow,
    true,
    false,
    vilc,
  );
  card("filter", "Prioritization\nFilter", "prioritization", layout.filter);
  card(
    "recovery",
    "Financial\nRecovery Test",
    "recovery",
    layout.recovery,
    false,
    muted,
  );
  const labels = vilc
    ? ["Optimize", "Keep", "Accelerate", "Keep", "Optimize", "Delist"]
    : ["Keep", "Optimize", "Accelerate", "Keep", "Optimize", "Delist"];
  const ids = vilc
    ? [
        "high-optimize",
        "high-keep",
        "accelerate",
        "low-keep",
        "low-optimize",
        "delist",
      ]
    : [
        "high-keep",
        "high-optimize",
        "accelerate",
        "low-keep",
        "low-optimize",
        "delist",
      ];
  labels.forEach((label, index) =>
    graph.outcomes.push({
      id: ids[index],
      label:
        version === "V2"
          ? ((
              { Optimize: "DTP", Accelerate: "Grow" } as Record<string, string>
            )[label] ?? label)
          : label,
      icon: label.toLowerCase(),
      bounds: [...layout.outcomes[index], 212, 71],
      value: value("outcomes", ids[index]),
      warning: false,
      muted: false,
      threshold: false,
    }),
  );
  return graph;
}
